import React, { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/react-hooks';
import { datadogLogs } from '@datadog/browser-logs';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import gql from 'graphql-tag';
import jwtDecode from 'jwt-decode';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';

import 'react-select/dist/react-select.css';
import * as actions from 'actions';
import { getMainAppDataLazyQuery } from 'API/MainAppData';
import LicensesDispatcher from 'Components/LicensesDispatcher';
import HeaderBar from 'Components-redesign/HeaderBar';
import Loader from 'Components-redesign/Loader';
import Notice from 'Components-redesign/Notice';
import PermissionChecker from 'Components-redesign/PermissionChecker';
import { SUBUSER_TOKEN_KEY } from 'consts';
import { useDispatch, useSelector } from 'Hooks';
import AnonymousAdyenKYC from 'Scenes/AdyenAnonymousKYC';
import ResetPassword from 'Scenes/ResetPassword';
import FourOhFour from 'Scenes-redesign/FourOhFour';
import store from 'store';
import { canAccessPrivateMenuItems } from 'utils/userRoleUtils';

import { REFRESH_TOKEN } from '../../API/MainAppData/queries';
import Drawer from '../Drawer';
import IntegrationsDispatcher from '../IntegrationsDispatcher';
import { GetCompany } from './__queries__';
import DisabledFeature from './DisabledFeature';
import NonPrivateRoutes from './NonPrivateRoutes';
import Routes from './Routes';
import { LoaderWrapper, Wrapper } from './styles';

const COMPANY_QUERY = gql`
  query GetCompany($id: ID!) {
    Company(id: $id) {
      id
      createdAt
      name
      currencies
      defaultCurrency
      defaultLanguage
      country
      timezone
      languages
      visibleVMCategories
      manualEndOfDay
      hasInventory
      hasMembershipCards
      hasPOS
      hasPoints
      hasTables
      hasAppOrders
      hasKDS
      hasCDS
      hasExpress
      useNewLicenses
    }
  }
`;

// Iitiate dayjs
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

const TOKEN_KEY = `${process.env.REACT_APP_PROJECT_ID}-idToken`;

interface AuthToken {
  exp: number;
  iat: number;
  projectId?: string;
  userId: string;
  modelName: string;
}

function hashAuthTokenExpired(authToken: string) {
  let token: AuthToken | null = null;
  try {
    token = jwtDecode<AuthToken>(authToken);
  } catch (e) {
    console.error(e);
  }
  const now = +new Date() / 1000;
  if (!token) {
    return true;
  }
  return token.exp < now;
}

const App = () => {
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.company);
  const subuserAuthToken = useSelector((state) => state.subuserAuthToken);
  const authToken = useSelector((state) => state.authToken);
  const location = useLocation();
  const [updatedToken, setUpdatedToken] = useState<boolean>(false);

  const [getMainAppData, mainAppDataQuery] = getMainAppDataLazyQuery();

  const companyQuery = useQuery<GetCompany>(COMPANY_QUERY, {
    variables: { id: companyId },
  });
  const currentCompany = companyQuery.data?.Company;

  const [refreshToken] = useMutation(REFRESH_TOKEN, {
    onCompleted: (data) => {
      if (data.refreshToken.success && data.refreshToken.token) {
        store.dispatch(actions.setToken(data.refreshToken.token));
      }
    },
  });

  useEffect(() => {
    dayjs.tz.setDefault(currentCompany?.timezone || 'Europe/Stockholm');
    dispatch(actions.setCompanyData(currentCompany || null));
  }, [currentCompany]);

  const exit = () => {
    localStorage.removeItem(SUBUSER_TOKEN_KEY);
    dispatch(actions.setSubuserToken(null));
  };

  /**
   * Handles reading the subuser token from local storage and setting it in the store.
   *
   * If the token is too old, we exit back to super admin mode.
   */
  useEffect(() => {
    const currentToken = localStorage.getItem(SUBUSER_TOKEN_KEY) || 'null';
    const currentTokenParsed = JSON.parse(currentToken);
    const hour = 1000 * 60 * 60;
    const now = Date.now();
    const anHourAgo = now - hour;

    if (!subuserAuthToken && (!currentTokenParsed || currentTokenParsed.timestamp < anHourAgo)) {
      exit();
    } else if (!subuserAuthToken && currentTokenParsed?.timestamp > anHourAgo) {
      dispatch(actions.setSubuserToken(currentTokenParsed.value));
    } else if (subuserAuthToken && subuserAuthToken !== currentTokenParsed?.value) {
      localStorage.setItem(
        SUBUSER_TOKEN_KEY,
        JSON.stringify({ value: subuserAuthToken, timestamp: now }),
      );
    }

    if (process.env.REACT_APP_PROJECT_ID === 'bae-mingo-development') {
      document.title = `D|${document.title}`;
    }

    getMainAppData();
  }, [subuserAuthToken]);

  const user = mainAppDataQuery.data?.user;
  const hasSuperAdminRole = canAccessPrivateMenuItems(user?.role ?? '');
  const isSuperAdmin = !!(hasSuperAdminRole || subuserAuthToken);
  const currentAuthToken = localStorage.getItem(TOKEN_KEY);

  const loading = mainAppDataQuery.loading || companyQuery.loading;

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    dispatch(actions.setToken(null));
  };

  useEffect(() => {
    if (!authToken) {
      logout();
    } else if (authToken !== currentAuthToken) {
      localStorage.setItem(TOKEN_KEY, authToken);

      ReactGA.set({ userId: isSuperAdmin ? 'SUPER_ADMIN' : user?.company?.name });
    }
  }, [authToken]);

  /**
   * Refreshes the token on boot
   */
  useEffect(() => {
    // Refresh token for the logged in admin account
    if (authToken === currentAuthToken && !loading && !updatedToken && isSuperAdmin) {
      refreshToken();
      setUpdatedToken(true);
    }
  }, [loading]);

  /**
   * Set the company ID to the user's company ID if it's not a super admin
   */
  useEffect(() => {
    if (!isSuperAdmin && user) {
      dispatch(actions.setCompany(user.company.id));
    }
  }, [user]);

  /**
   * Set the company data in the store after it has loaded
   */
  useEffect(() => {
    if (!currentCompany || !user) {
      if (user) {
        dispatch(actions.setUserRole(user.role));
      }
      return;
    }

    const languages = currentCompany.languages || user.company.languages;
    const currencies = currentCompany.currencies || [];
    const defaultCurrency = currentCompany.defaultCurrency || '';

    if (!companyId) {
      dispatch(actions.setCompany(user.company.id));
    }

    if (isSuperAdmin) {
      const isBarControl = user.email.includes('barcontrol.no');

      const spanishKeys = ['+spain@baemingo.com', 'sodire', 'yexian'];
      const isSpain = spanishKeys.some((key) => user.email.includes(key));

      const superAdminUserType = isBarControl ? 'BARCONTROL' : isSpain ? 'SPAIN' : 'BAEMINGO';

      dispatch(actions.setSuperAdminUserType(superAdminUserType));
    }

    dispatch(actions.setUserId(user.id));
    dispatch(actions.setUserRole(user.role));
    dispatch(actions.setUserPermissions(user.permissions));
    dispatch(actions.setLanguages(languages));
    dispatch(actions.setCurrencies(currencies));
    dispatch(actions.setCurrency(defaultCurrency));
  }, [user, currentCompany]);

  const isAuthToken = (authToken: string | null) => {
    if (!authToken) {
      return false;
    }

    const isExpired = hashAuthTokenExpired(authToken);

    if (isExpired) {
      logout();
      return false;
    }

    return true;
  };

  const isToken = isAuthToken(authToken);

  const handleLogin = () => {
    getMainAppData();
  };

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname });
  }, [location.pathname]);

  if (loading || mainAppDataQuery.error) {
    return (
      <LoaderWrapper>
        <Loader color="#1C232C" />
      </LoaderWrapper>
    );
  }
  const currentPath = location.pathname.split('/')[1];
  const sectionPath = location.pathname.split('/')[2];

  // This is an anonymous URL that we can send to customers so they can perform KYC
  if (currentPath.includes('know-your-customer')) {
    const url = new URLSearchParams(window.location.search);
    return <AnonymousAdyenKYC companyId={url.get('c')!} accountId={url.get('a')!} />;
  }

  if (!isToken || !user) {
    return <NonPrivateRoutes onRedirectEnabled={handleLogin} />;
  }

  if (user.shouldUpdatePassword) {
    return <ResetPassword onSuccess={() => mainAppDataQuery.refetch()} />;
  }

  const isPosInaccassable = currentPath.includes('pos') && currentCompany && !currentCompany.hasPOS;
  const isPayoutsInaccassable =
    currentPath.includes('payouts-beta') &&
    currentCompany &&
    !currentCompany.visibleVMCategories.includes('payouts-beta');

  let isAccessible = true;
  if (
    (currentPath.includes('tsr') && currentCompany && !currentCompany.hasTables) ||
    (currentPath.includes('express') && currentCompany && !currentCompany.hasExpress) ||
    (currentPath.includes('inventory') && currentCompany && !currentCompany.hasInventory) ||
    (currentPath.includes('app') &&
      currentCompany &&
      !currentCompany.hasAppOrders &&
      !currentPath.includes('app-pos-report') &&
      !sectionPath?.includes('shopping-carts')) ||
    (sectionPath?.includes('shopping-carts') && !isSuperAdmin) ||
    (currentPath.includes('gift-cards/admin') && !isSuperAdmin)
  ) {
    isAccessible = false;
  }

  // Check if company should have access to beta features
  const found = currentCompany?.visibleVMCategories.join().includes(currentPath);
  if (!found && currentPath.includes('beta') && !isSuperAdmin) {
    isAccessible = false;
  }

  // Add the current logged in user's company as metadata
  datadogLogs.addLoggerGlobalContext('company', currentCompany?.name);

  return (
    <>
      <Drawer routes={currentCompany?.visibleVMCategories || []} />
      <HeaderBar
        companyName={currentCompany?.name || ''}
        userName={user.name || ''}
        userRole={user.role}
        defaultLanguage={currentCompany?.defaultLanguage || 'en'}
      />

      <Wrapper isSuperAdmin={!subuserAuthToken}>
        {isPosInaccassable ? (
          <DisabledFeature feature="POS" />
        ) : isPayoutsInaccassable ? (
          <DisabledFeature feature="Payouts Beta" />
        ) : !isAccessible ? (
          <FourOhFour />
        ) : (
          <PermissionChecker>
            <Routes />
          </PermissionChecker>
        )}
      </Wrapper>

      <Notice />

      <IntegrationsDispatcher />
      <LicensesDispatcher />
    </>
  );
};

export default App;
